'use client';

/**
 * PageFooter — renders the correct footer variant for the current route.
 *
 * Sub-menu layouts (Training, Settings, Call Metrics, etc.) render this
 * inside their content column so the footer doesn't span the sub-sidebar.
 * They also call `useClaimFooter()` to tell AppShell not to render its own copy.
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAppShell } from '../AppShellContext';
import { AppFooter } from './AppFooter';
import { FooterMinimal } from './FooterMinimal';

// ---------------------------------------------------------------------------
// Footer Claim Context — lets child layouts suppress AppShell's footer
// ---------------------------------------------------------------------------

interface FooterClaimCtx {
  /** True when a child layout is rendering the footer itself */
  claimed: boolean;
  /** Call on mount to claim; returns cleanup fn */
  claim: () => () => void;
}

const FooterClaimContext = createContext<FooterClaimCtx>({
  claimed: false,
  claim: () => () => {},
});

export function FooterClaimProvider({ children }: { children: React.ReactNode }) {
  const [claimed, setClaimed] = useState(false);
  const claimedRef = useRef(false);

  const claim = useCallback(() => {
    // Guard: only set state if not already claimed (prevents re-render loops)
    if (!claimedRef.current) {
      claimedRef.current = true;
      setClaimed(true);
    }
    return () => {
      claimedRef.current = false;
      setClaimed(false);
    };
  }, []);

  return (
    <FooterClaimContext.Provider value={{ claimed, claim }}>
      {children}
    </FooterClaimContext.Provider>
  );
}

/** Returns true if a child layout has claimed footer rendering */
export function useIsFooterClaimed(): boolean {
  return useContext(FooterClaimContext).claimed;
}

/** Call in useEffect to claim footer rendering for your layout */
export function useClaimFooter(): void {
  const { claim } = useContext(FooterClaimContext);
  useEffect(() => claim(), [claim]);
}

// ---------------------------------------------------------------------------
// PageFooter Component
// ---------------------------------------------------------------------------

export function PageFooter() {
  const { currentRoute } = useAppShell();
  const footer = currentRoute?.footer || 'hidden';
  if (footer === 'hidden') return null;
  return footer === 'minimal' ? <FooterMinimal /> : <AppFooter />;
}
